import React, { useMemo } from 'react';
import { Button, DatePicker, Input, List, Select } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import locale from 'antd/es/date-picker/locale/zh_CN';
import 'moment/locale/zh-cn';
import moment from 'moment';
import {
  POLICY_LEVEL_LABELS,
  POLICY_LEVEL_OPTIONS,
} from '../../constants/policyFilters';
import { DEFAULT_POLICY_FILTER, type Policy, type PolicyFilter } from '../../types';
import { getUniquePolicyFieldValues } from '../../utils/policyHelpers';

export interface PolicySidebarProps {
  policies: Policy[];
  allPolicies: Policy[];
  statistics: {
    total: number;
    national: number;
    provincial: number;
    city: number;
    park: number;
    local: number;
  };
  filter: PolicyFilter;
  setFilter: Dispatch<SetStateAction<PolicyFilter>>;
  selectedPolicyId: string | null | undefined;
  onSelectPolicy: (policyId: string) => void;
}

const panelClass = 'dash-panel dash-panel--left';

export function PolicySidebar(props: PolicySidebarProps): React.ReactElement {
  const {
    policies,
    allPolicies,
    statistics,
    filter,
    setFilter,
    selectedPolicyId,
    onSelectPolicy,
  } = props;

  const provinceOptions = useMemo(
    () =>
      getUniquePolicyFieldValues(allPolicies, 'province').map((value) => ({
        label: value,
        value,
      })),
    [allPolicies],
  );

  return (
    <aside className={panelClass}>
      <div className="dash-section">
        <div className="dash-stat-grid">
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">国家级政策</div>
            <div className="dash-stat-card__value">
              {statistics.national}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">省级政策</div>
            <div className="dash-stat-card__value dash-stat-card__value--accent">
              {statistics.provincial}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">市级政策</div>
            <div className="dash-stat-card__value dash-stat-card__value--pending">
              {statistics.city}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">园区级政策</div>
            <div className="dash-stat-card__value">
              {statistics.park}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-section">
        <div className="dash-field-row">
          <div className="dash-field-label">政策关键词</div>
          <Button
            type="link"
            size="small"
            className="dash-inline-link-btn"
            onClick={() => setFilter(DEFAULT_POLICY_FILTER)}
          >
            重置筛选
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={filter.keyword}
            placeholder="请输入政策标题或关键词"
            onChange={(e) =>
              setFilter((prev) => ({
                ...prev,
                keyword: e.target.value,
              }))
            }
          />
          <Button
            type="default"
            disabled={!filter.keyword.trim()}
            onClick={() =>
              setFilter((prev) => ({
                ...prev,
                keyword: '',
              }))
            }
          >
            清空
          </Button>
        </div>

        <div className="dash-field-label dash-field-label--spaced">政策层级</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.level}
          onChange={(value) =>
            setFilter((prev) => ({
              ...prev,
              level: value,
              province: value === 'provincial' || value === 'city' ? prev.province : undefined,
              city: value === 'provincial' || value === 'city' ? prev.city : undefined,
            }))
          }
          options={POLICY_LEVEL_OPTIONS}
        />

        {filter.level === 'provincial' || filter.level === 'city' ? (
          <>
            <div className="dash-field-label dash-field-label--spaced">省份</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              className="dash-select"
              dropdownClassName="dash-select-dropdown"
              style={{ width: '100%' }}
              value={filter.province}
              placeholder="全部省份"
              onChange={(value) =>
                setFilter((prev) => ({
                  ...prev,
                  province: value ?? undefined,
                  city: undefined,
                }))
              }
              options={provinceOptions}
            />
          </>
        ) : null}

        <div className="dash-field-label dash-field-label--spaced">发布时间范围</div>
        <div className="dash-field-row" style={{ gap: 6, alignItems: 'center' }}>
          <DatePicker
            locale={locale}
            allowClear
            placeholder="开始日期"
            style={{ flex: 1 }}
            value={filter.startDate ? moment(filter.startDate, 'YYYY-MM-DD') : null}
            onChange={(date) =>
              setFilter((prev) => ({
                ...prev,
                startDate: date ? date.format('YYYY-MM-DD') : undefined,
              }))
            }
          />
          <span style={{ color: 'rgba(255,255,255,0.45)' }}>~</span>
          <DatePicker
            locale={locale}
            allowClear
            placeholder="结束日期"
            style={{ flex: 1 }}
            value={filter.endDate ? moment(filter.endDate, 'YYYY-MM-DD') : null}
            onChange={(date) =>
              setFilter((prev) => ({
                ...prev,
                endDate: date ? date.format('YYYY-MM-DD') : undefined,
              }))
            }
          />
        </div>

      </div>

      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">政策列表（{policies.length}）</div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={policies}
            locale={{ emptyText: '未找到符合条件的政策' }}
            renderItem={(policy) => {
              const isSelected = selectedPolicyId === policy.id;
              const regionText = [policy.province, policy.city || policy.parkName]
                .filter(Boolean)
                .join(' / ');
              return (
                <List.Item
                  className={
                    isSelected ? 'dash-list-item dash-list-item--active' : 'dash-list-item'
                  }
                  onClick={() => onSelectPolicy(policy.id)}
                >
                  <div className="dash-policy-list-item">
                    <div className="dash-policy-list-item__head">
                      <span
                        className={`dash-level-badge dash-level-badge--${policy.level}`}
                      >
                        {POLICY_LEVEL_LABELS[policy.level]}
                      </span>
                      <span className="dash-policy-list-item__date">{policy.issuedAt}</span>
                    </div>
                    <div className="dash-list-item__name">{policy.title}</div>
                    <div className="dash-policy-list-item__meta">
                      <span>{regionText || '全国适用'}</span>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}
